import { useState } from "react";
import {
  Avatar,
  Box,
  IconButton,
  Paper,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import NotesCard from "./NotesCard";
import type { OrganizerCardProps } from "../../../types/types";
import { LocationIcon } from "../../../../../../components/icons";
import { tokens } from "../../../../../../theme";
import { WhatsApp, Phone } from "@mui/icons-material";
import ContactDetailsDialog from "./ContactDetailsDialog";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../../store/Index";

interface ClientInfoCardProps {
  client?: OrganizerCardProps;
  currentUserName?: string;
  onViewAllBookings?: () => void;
}

export default function ClientInfoCard({
  client,
  currentUserName = "Muzukuru Staff",
  onViewAllBookings,
}: ClientInfoCardProps) {
  const theme = useTheme();
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const colors = tokens(theme.palette.mode, reduxTheme);
  const navigate = useNavigate();

  const [isContactDialogOpen, setIsContactDialogOpen] = useState(false);

  const getInitials = (name?: string) => {
    if (!name) return "";
    const parts = name.trim().split(" ");
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return parts[0][0].toUpperCase() + parts[parts.length - 1][0].toUpperCase();
  };

  const handleCall = () => {
    if (client?.phone) window.location.href = `tel:${client.phone}`;
  };

  const handleWhatsApp = () => {
    const phone = client?.phone?.replace(/\D/g, "") || "";
    const msg = `Hi ${client?.name || "Client"}, this is ${currentUserName} from Muzukuru Funeral Streaming.`;
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  return (
    <>
      <Paper
        sx={{
          flex: 1,
          borderRadius: "12px",
          p: 2,
          boxShadow: 0,
          height: "100%",
          bgcolor: colors.primary[900],
        }}
      >
        <Stack spacing={2}>
          {/* Header — clickable to open contact dialog */}
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            onClick={() => setIsContactDialogOpen(true)}
            sx={{ cursor: "pointer", "&:hover": { opacity: 0.85 } }}
          >
            <Avatar
              src={client?.contact_image || undefined}
              sx={{
                width: 56,
                height: 56,
                bgcolor: colors.primary[400],
                fontWeight: 700,
                fontSize: "14px",
              }}
            >
              {!client?.contact_image && getInitials(client?.name)}
            </Avatar>

            <Box sx={{ flex: 1 }}>
              <Typography sx={{ fontWeight: 700, fontSize: "18px", color: colors.primary[700] }}>
                {client?.name}
              </Typography>
              <Typography sx={{ fontWeight: 400, fontSize: "14px", color: colors.primary[300] }}>
                {client?.relation}
              </Typography>
            </Box>

            {/* Stop propagation so action buttons don't open the dialog */}
            <Stack direction="row" spacing={1} onClick={(e) => e.stopPropagation()}>
              <IconButton
                onClick={handleCall}
                disabled={!client?.phone}
                sx={{
                  bgcolor: colors.secondary[500] + "50",
                  width: 30,
                  height: 30,
                  "&:hover": { bgcolor: colors.secondary[500] },
                }}
              >
                <Phone sx={{ fontSize: 16, color: colors.primary[400] }} />
              </IconButton>

              <IconButton
                onClick={handleWhatsApp}
                disabled={!client?.phone}
                sx={{
                  bgcolor: colors.secondary[500] + "50",
                  width: 30,
                  height: 30,
                  "&:hover": { bgcolor: colors.secondary[500] },
                }}
              >
                <WhatsApp sx={{ fontSize: 16, color: colors.primary[400] }} />
              </IconButton>
            </Stack>
          </Stack>

          {client?.address?.trim() && client.address !== "N/A" && (
            <Box
              sx={{
                bgcolor: colors.primary[800],
                borderRadius: "20px",
                display: "flex",
                alignItems: "center",
                p: 1.5,
                gap: 1,
              }}
            >
              <LocationIcon sx={{ fontSize: 16, color: colors.primary[300] }} />
              <Typography sx={{ fontSize: "13px", color: colors.primary[700] }}>
                {client.address}
              </Typography>
            </Box>
          )}

          {client?.source_of_booking === "referral" && client.referral_partner_name && (
            <Box
              sx={{
                bgcolor: colors.primary[800],
                borderRadius: "12px",
                p: 1.5,
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <Typography sx={{ fontSize: "13px", color: colors.primary[300] }}>
                Referred by
              </Typography>
              <Typography sx={{ fontSize: "13px", fontWeight: 600, color: colors.primary[700] }}>
                {client.referral_partner_name}
              </Typography>
            </Box>
          )}

          <NotesCard title="Special Notes" content={client?.specialNotes || ""} />
          <NotesCard title="Notes" content={client?.notes || ""} />
        </Stack>
      </Paper>

      <ContactDetailsDialog
        open={isContactDialogOpen}
        client={client}
        currentUserName={currentUserName}
        onClose={() => setIsContactDialogOpen(false)}
        onBookingClick={(bookingId) => {
          setIsContactDialogOpen(false);
          navigate(`/bookings/${bookingId}`);
        }}
        onViewAllBookings={() => {
          setIsContactDialogOpen(false);
          onViewAllBookings?.();
        }}
      />
    </>
  );
}